/**
 * ImageUploadDropzone - Enhanced drag-and-drop upload component
 * Styled for high-end UI/UX with visual feedback
 */

import React, { useCallback } from 'react';
import Dropzone, { DropzoneState } from 'react-dropzone';
import { Box, Typography, Stack, Avatar, SvgIcon, alpha, useTheme } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { ImageUploadDropzoneProps } from './types';

const ImageUploadDropzone: React.FC<ImageUploadDropzoneProps> = ({
  onDrop,
  accept,
  maxSize,
  maxFiles = 1,
  disabled = false,
  compact = false,
  width,
  height,
  label,
}) => {
  const theme = useTheme();

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        // Limit to maxFiles if specified
        const filesToProcess = maxFiles > 1
          ? acceptedFiles.slice(0, maxFiles)
          : acceptedFiles.slice(0, 1);
        onDrop(filesToProcess);
      }
    },
    [onDrop, maxFiles]
  );

  // Convert accept array to comma-separated string for react-dropzone v10
  const acceptString = accept.join(',');

  return (
    // @ts-ignore - react-dropzone v10 accepts string for accept prop
    <Dropzone
      onDrop={handleDrop}
      accept={acceptString}
      maxSize={maxSize}
      disabled={disabled}
      multiple={maxFiles > 1}
    >
      {({ getRootProps, getInputProps, isDragActive, isDragReject }: DropzoneState) => {
        const getBorderColor = () => {
          if (isDragReject) return theme.palette.error.main;
          if (isDragActive) return theme.palette.primary.main;
          if (disabled) return theme.palette.action.disabled;
          return theme.palette.divider;
        };

        const getBackgroundColor = () => {
          if (isDragReject) return alpha(theme.palette.error.main, 0.08);
          if (isDragActive) return alpha(theme.palette.primary.main, 0.08);
          return 'transparent';
        };

        return (
          <Box
            {...getRootProps()}
            sx={{
              alignItems: 'center',
              border: 2,
              borderRadius: 2,
              borderStyle: 'dashed',
              borderColor: getBorderColor(),
              backgroundColor: getBackgroundColor(),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              outline: 'none',
              p: compact ? 2 : 4,
              width: width || '100%',
              height: height || (compact ? 120 : 'auto'),
              minHeight: compact ? 100 : 140,
              transition: 'all 0.2s ease-in-out',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              '&:hover': {
                borderColor: disabled ? getBorderColor() : theme.palette.primary.main,
                backgroundColor: disabled
                  ? getBackgroundColor()
                  : alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            <input {...getInputProps()} />

            {compact ? (
              // Compact mode for inline dropzones (gallery add button)
              <Stack alignItems="center" spacing={1}>
                <Avatar
                  sx={{
                    bgcolor: isDragActive
                      ? alpha(theme.palette.primary.main, 0.12)
                      : alpha(theme.palette.grey[500], 0.12),
                    height: 40,
                    width: 40,
                    transition: 'all 0.2s',
                  }}
                >
                  <AddPhotoAlternateIcon
                    sx={{
                      color: isDragActive
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      fontSize: 24,
                    }}
                  />
                </Avatar>
                <Typography
                  variant="caption"
                  color={isDragActive ? 'primary' : 'text.secondary'}
                  sx={{ textAlign: 'center' }}
                >
                  {isDragActive ? 'Drop here' : label || 'Add image'}
                </Typography>
              </Stack>
            ) : (
              // Full mode with icon and text
              <Stack alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: isDragActive
                      ? alpha(theme.palette.primary.main, 0.12)
                      : alpha(theme.palette.grey[500], 0.08),
                    height: 64,
                    width: 64,
                    transition: 'all 0.2s',
                  }}
                >
                  <SvgIcon
                    sx={{
                      color: isDragActive
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      fontSize: 32,
                    }}
                  >
                    <CloudUploadOutlinedIcon />
                  </SvgIcon>
                </Avatar>

                <Stack spacing={0.5} alignItems="center">
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      color: isDragActive ? 'primary.main' : 'text.primary',
                      '& span': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      },
                    }}
                  >
                    {isDragActive ? (
                      'Drop your image here'
                    ) : (
                      <>
                        <span>Click to upload</span> or drag and drop
                      </>
                    )}
                  </Typography>

                  {label && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center' }}
                    >
                      {label}
                    </Typography>
                  )}

                  {maxFiles > 1 && (
                    <Typography variant="caption" color="text.secondary">
                      You can upload up to {maxFiles} images
                    </Typography>
                  )}
                </Stack>
              </Stack>
            )}
          </Box>
        );
      }}
    </Dropzone>
  );
};

export default ImageUploadDropzone;
